/**
 * IndexedDB caching layer — persistent, async, high-capacity cache
 * for org users, templates, and API responses.
 *
 * Database: `ulgebra-cache` (version 1)
 * Object stores: org_data, templates, proxy_cache
 *
 * Fallback: If IndexedDB is unavailable, uses an in-memory Map with
 * identical TTL semantics (no persistence, no errors).
 */

import { networkStats } from '@/lib/network-stats';

// ── Types ────────────────────────────────────────────────────────

export interface CacheEntry<T = unknown> {
  key: string;
  data: T;
  expiresAt: number;
  createdAt: number;
}

export type StoreName = 'org_data' | 'templates' | 'proxy_cache';

const DB_NAME = 'ulgebra-cache';
const DB_VERSION = 1;
const STORE_NAMES: StoreName[] = ['org_data', 'templates', 'proxy_cache'];

// ── Database singleton ───────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;
let fallbackMode = false;
const fallbackMap = new Map<string, CacheEntry>();

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        for (const name of STORE_NAMES) {
          if (!db.objectStoreNames.contains(name)) {
            const store = db.createObjectStore(name, { keyPath: 'key' });
            store.createIndex('expiresAt', 'expiresAt', { unique: false });
          }
        }
      };

      request.onsuccess = () => {
        const db = request.result;
        // If the connection closes (e.g. another tab upgrades the DB),
        // reset the singleton so the next call re-opens a fresh connection.
        db.onclose = () => {
          console.warn('[IDB] Connection closed unexpectedly — will re-open on next access');
          dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
          console.warn('[IDB] Version change detected — closed connection');
        };
        console.log('[IDB] Database opened successfully');
        resolve(db);
      };

      request.onerror = () => {
        console.warn('[IDB] Failed to open database, using fallback', request.error);
        fallbackMode = true;
        reject(request.error);
      };
    } catch (err) {
      console.warn('[IDB] IndexedDB not available, using fallback', err);
      fallbackMode = true;
      reject(err);
    }
  }).catch(() => {
    // Swallow the rejection — fallbackMode is already set
    fallbackMode = true;
    return null as unknown as IDBDatabase;
  });

  return dbPromise;
}

/**
 * Get a valid DB handle. If the cached connection is stale (closing/closed),
 * resets the singleton and re-opens. Returns null if IDB is unavailable.
 */
async function getDB(): Promise<IDBDatabase | null> {
  try {
    const db = await openDB();
    if (!db) return null;
    // Quick sanity check — try creating a tiny transaction.
    // If the connection is closing this throws InvalidStateError.
    db.transaction(STORE_NAMES[0], 'readonly');
    return db;
  } catch {
    // Connection is stale — reset and retry once
    dbPromise = null;
    try {
      const db = await openDB();
      return db || null;
    } catch {
      return null;
    }
  }
}

// ── Fallback helpers ─────────────────────────────────────────────

function fallbackKey(store: StoreName, key: string): string {
  return `${store}::${key}`;
}

// ── Public API ───────────────────────────────────────────────────

export const idbCache = {
  /**
   * Get a cached entry. Returns null if not found or expired.
   * Automatically counts as an IDB cache hit in network stats.
   */
  async get<T>(store: StoreName, key: string): Promise<T | null> {
    if (fallbackMode) {
      const entry = fallbackMap.get(fallbackKey(store, key)) as CacheEntry<T> | undefined;
      if (entry && Date.now() < entry.expiresAt) {
        networkStats.idbCacheHit();
        return entry.data;
      }
      if (entry) fallbackMap.delete(fallbackKey(store, key));
      return null;
    }

    try {
      const db = await getDB();
      if (!db) return null;

      return new Promise<T | null>((resolve) => {
        const tx = db.transaction(store, 'readonly');
        const objStore = tx.objectStore(store);
        const request = objStore.get(key);

        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          if (entry && Date.now() < entry.expiresAt) {
            networkStats.idbCacheHit();
            resolve(entry.data);
          } else {
            // Expired — schedule async cleanup
            if (entry) {
              idbCache.delete(store, key).catch(() => {});
            }
            resolve(null);
          }
        };

        request.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  },

  /** Get the full cache entry (with metadata). Returns null if not found or expired. */
  async getEntry<T>(store: StoreName, key: string): Promise<CacheEntry<T> | null> {
    if (fallbackMode) {
      const entry = fallbackMap.get(fallbackKey(store, key)) as CacheEntry<T> | undefined;
      if (entry && Date.now() < entry.expiresAt) return entry;
      return null;
    }
    try {
      const db = await getDB();
      if (!db) return null;
      return new Promise<CacheEntry<T> | null>((resolve) => {
        const tx = db.transaction(store, 'readonly');
        const request = tx.objectStore(store).get(key);
        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;
          resolve(entry && Date.now() < entry.expiresAt ? entry : null);
        };
        request.onerror = () => resolve(null);
      });
    } catch { return null; }
  },

  /** Store a value with a TTL (in milliseconds). */
  async set<T>(store: StoreName, key: string, data: T, ttlMs: number): Promise<void> {
    const entry: CacheEntry<T> = {
      key,
      data,
      expiresAt: Date.now() + ttlMs,
      createdAt: Date.now(),
    };

    if (fallbackMode) {
      fallbackMap.set(fallbackKey(store, key), entry as CacheEntry);
      return;
    }

    try {
      const db = await getDB();
      if (!db) return;

      return new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).put(entry);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silently fail — cache write is non-critical
    }
  },

  /** Delete a single entry. */
  async delete(store: StoreName, key: string): Promise<void> {
    if (fallbackMode) {
      fallbackMap.delete(fallbackKey(store, key));
      return;
    }

    try {
      const db = await getDB();
      if (!db) return;

      return new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silently fail
    }
  },

  /** Clear all entries in a store. */
  async clear(store: StoreName): Promise<void> {
    if (fallbackMode) {
      const prefix = `${store}::`;
      for (const k of fallbackMap.keys()) {
        if (k.startsWith(prefix)) fallbackMap.delete(k);
      }
      return;
    }

    try {
      const db = await getDB();
      if (!db) return;

      return new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silently fail
    }
  },

  /** Purge all expired entries from one or all stores. */
  async clearExpired(store?: StoreName): Promise<void> {
    const stores = store ? [store] : STORE_NAMES;

    if (fallbackMode) {
      const now = Date.now();
      for (const [k, v] of fallbackMap.entries()) {
        if (now >= v.expiresAt) fallbackMap.delete(k);
      }
      return;
    }

    try {
      const db = await getDB();
      if (!db) return;

      for (const s of stores) {
        await new Promise<void>((resolve) => {
          const tx = db.transaction(s, 'readwrite');
          const objStore = tx.objectStore(s);
          const index = objStore.index('expiresAt');
          const range = IDBKeyRange.upperBound(Date.now());
          const cursorReq = index.openCursor(range);

          cursorReq.onsuccess = () => {
            const cursor = cursorReq.result;
            if (cursor) {
              cursor.delete();
              cursor.continue();
            }
          };

          tx.oncomplete = () => resolve();
          tx.onerror = () => resolve();
        });
      }
    } catch {
      // Silently fail
    }
  },

  /**
   * Clear entries whose key contains the given pattern (substring match).
   * Used for selective proxy cache invalidation by URL.
   */
  async clearByKeyPattern(store: StoreName, pattern: string): Promise<void> {
    if (fallbackMode) {
      const prefix = `${store}::`;
      for (const k of fallbackMap.keys()) {
        if (k.startsWith(prefix) && k.includes(pattern)) {
          fallbackMap.delete(k);
        }
      }
      return;
    }

    try {
      const db = await getDB();
      if (!db) return;

      await new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        const objStore = tx.objectStore(store);
        const cursorReq = objStore.openCursor();

        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor) {
            const entry = cursor.value as CacheEntry;
            // For proxy_cache, keys are base64-encoded — decode and check
            try {
              const decoded = atob(entry.key);
              if (decoded.includes(pattern)) {
                cursor.delete();
              }
            } catch {
              // Not base64 — do plain substring match
              if (entry.key.includes(pattern)) {
                cursor.delete();
              }
            }
            cursor.continue();
          }
        };

        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      // Silently fail
    }
  },
};

// ── Auto-purge expired entries on load (non-blocking) ────────────
// Run once after DB opens to clean stale data.
openDB().then(() => {
  if (!fallbackMode) {
    idbCache.clearExpired().catch(() => {});
  }
});

// Legacy localStorage cleanup removed — migration to IDB is complete.
// No need to wipe ua_cache_* keys on every page load.
